import React, { useState, useEffect, useMemo } from "react";
import { fetchJobDetails, updateReCallVisitStatus } from "./service";
import OffcanvasTemplate from "../../../common/offcanvasTemplate";
import { theme } from "../../../theme";
import BtnSpinner from "../../../common/btnSpinner";
import Bell from "./components/Bell";
import toast from "react-hot-toast";
import { showNotification } from "../../../common/notifications/notifications";
import { formatMoney } from "../../../utils";

const Html = ({ show, re_call_visit_id, handleClose }) => {
    const [reCallVisitDetails, setReCallVisitDetails] = useState({
        loading: false,
        reCallVisit: null,
        ratePerHour: null,
        error: null
    });

    useEffect(() => {
        if (show && re_call_visit_id) {
            handleFetchReCallVisitDetails();
        }
    }, [show, re_call_visit_id]);

    const handleFetchReCallVisitDetails = async () => {
        setReCallVisitDetails(prev => ({ ...prev, loading: true }));
        try {
            const response = await fetchJobDetails(re_call_visit_id);
            setReCallVisitDetails({ loading: false, reCallVisit: response.reCallVisit, ratePerHour: response.ratePerHour, error: null });
        } catch (error) {
            setReCallVisitDetails({ loading: false, reCallVisit: null, error: "Failed to load details" });
        }
    };

    const handleUpdateReCallVisitStatus = (status) => {
        showNotification({
            notificationType: 'UPDATE_RECALL_VISIT_STATUS',
            preConfirm: async () => await updateReCallVisitStatus(re_call_visit_id, status),
            isConfirmed: (result) => {
                toast[result.value.status === "Success" ? "success" : "error"](result.value.message);
                    handleClose();
            }
        });
    };

    const FooterBtns = useMemo(() => (
        <div className="offcanvas-footer">
            <div className="container">
                <div className="row mt-2">
                    <div className="col text-center mb-3">
                        <div className="d-grid gap-2">
                            {!reCallVisitDetails.reCallVisit ? (
                                <button
                                    type="button"
                                    className={`btn btn-${theme.close_btn} btn-sm w-100`}
                                    onClick={handleFetchReCallVisitDetails}
                                    disabled={reCallVisitDetails.loading}
                                >
                                    {reCallVisitDetails.loading ? <BtnSpinner /> : "VIEW DETAILS"}
                                </button>
                            ) : (
                                <div className="row">
                                    <div className="col-sm">
                                        <button
                                            type="button"
                                            className={`btn btn-${theme.action_btn} w-100 btn-sm`}
                                            onClick={() => handleUpdateReCallVisitStatus(1)}
                                        >
                                            <i className="material-symbols-rounded text-lg">check</i>&nbsp;Accept Job
                                        </button>
                                    </div>
                                    <div className="col-sm">
                                        <button
                                            type="button"
                                            className={`btn btn-${theme.reject_btn} w-100 btn-sm`}
                                            onClick={() => handleUpdateReCallVisitStatus(-1)}
                                        >
                                            <i className="material-symbols-rounded text-lg">close</i>&nbsp;Reject Job
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ), [reCallVisitDetails]);

    const JobDetails = useMemo(() => (
        <div className="card mt-4">
            <div className="card-header pb-0 p-3">
                <h6 className="mb-0">Job details</h6>
            </div>
            <div className="card-body p-3">
                <p className="text-sm">{reCallVisitDetails.reCallVisit?.job?.job_description}</p>
                <ul className="list-group">
                    <li className="list-group-item border-0 ps-0 pt-0 text-sm">
                        <strong className="text-dark">Address:</strong> {reCallVisitDetails.reCallVisit?.job?.job_address}
                    </li>
                    <li className="list-group-item border-0 ps-0 text-sm">
                        <strong className="text-dark">Postcode:</strong> {reCallVisitDetails.reCallVisit?.job?.job_postcode}
                    </li>
                    <li className="list-group-item border-0 ps-0 text-sm">
                        <strong className="text-dark">Rate per hour:</strong> &nbsp;
                        {reCallVisitDetails.reCallVisit?.re_call_visit_paid_type === 1 ? formatMoney(reCallVisitDetails.ratePerHour) : "Free"}
                    </li>
                </ul>
            </div>
        </div>
    ), [reCallVisitDetails]);

    const DisputeDetails = useMemo(() => (
        <div className="card mt-4">
            <div className="card-header pb-0 p-3">
                <h6 className="mb-0">{reCallVisitDetails.reCallVisit?.job?.dispute?.dispute_type?.dispute_type_text}</h6>
            </div>
            <div className="card-body p-3">
                <p className="text-sm">{reCallVisitDetails.reCallVisit?.job?.dispute?.dispute_text}</p>
                <div className="scroll-container mt-3">
                    {reCallVisitDetails.reCallVisit?.job?.dispute?.disputes_images?.length ? (
                        reCallVisitDetails.reCallVisit.job.dispute.disputes_images.map((image, index) => (
                            <img
                                key={index}
                                src={image?.disputes_image_path}
                                alt={`Image_${index + 1}`}
                                className="mb-1"
                            />
                        ))
                    ) : (
                        <p className="text-center pt-3">This job has no images</p>
                    )}
                </div>
            </div>
        </div>
    ), [reCallVisitDetails]);

    const FtmDetails = useMemo(() => (
        <div className="card card-body">
            <div className="row gx-4">
                <div className="col-auto">
                    <div className="avatar avatar-xl position-relative">
                        {reCallVisitDetails.reCallVisit?.job?.ftm?.ftm_profile_image_path && (
                            <img
                                src={reCallVisitDetails.reCallVisit?.job?.ftm?.ftm_profile_image_path}
                                alt="FTM Profile"
                                className="border-radius-lg w-100 h-100"
                            />
                        )}
                    </div>
                </div>
                <div className="col-auto my-auto">
                    <h5 className="mb-1">
                        {reCallVisitDetails.reCallVisit?.job?.ftm?.ftm_forename} {reCallVisitDetails.reCallVisit?.job?.ftm?.ftm_surname}
                    </h5>
                    <p className="mb-0 font-weight-normal text-sm">
                        {reCallVisitDetails.reCallVisit?.job?.ftm?.ftm_vehicle_number}
                    </p>
                </div>
            </div>
        </div>
    ), [reCallVisitDetails]);

    return (
        <OffcanvasTemplate
            open={show}
            handleClose={handleClose}
            placement="end"
            title={reCallVisitDetails.reCallVisit ? "A new re-call visit job requires your attention." : null}
            body={reCallVisitDetails.reCallVisit ? (
                <>
                    {FtmDetails}
                    {JobDetails}
                    {DisputeDetails}
                </>
            ) : <Bell />}
            footer={FooterBtns}
            showCloseButton={false}
        />
    );
};

export default Html;